import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from "../../../services/api";
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { BaseEcgView } from '../Ecg/BaseEcgView';
import { BaseMultipleChartsView } from '../Ecg/BaseMultipleChartsView'
import { OnlineDataPanel } from '../Ecg/OnlineDataPanel'
import { dateFormatter } from '../../../utils/dateFormatter'
import {recordLengthFormatter} from '../../../utils/recordLengthFormatter'

import RequestConsultationConfirmModal from '../../Consultation/RequestConsultationConfirmModal'
import ViewConsultationList from '../Consultation/ViewConsultationList'

import Loading from '../Loading'

import { AllProcessedData } from '../Processed/AllProcessedData'

import { UserViewOneRecordInfo } from './UserViewOneRecordInfo'
import RecordActionToolbar from './RecordActionToolbar'
import { useToastContext } from "../../../toasts/toast-context";
import { useNavigate } from 'react-router-dom';

import styles from './UserViewOneRecord.module.scss';


export default function UserViewOneRecord({ userUid, recordUid, baseOneRecordUrl, allowRequestConsultation, allowViewConsultation, allowEditMarkers, consultationUid, allowEditRecords, allowDeleteRecords, allowMoveRecords, getPageUserUrlTemplate }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessToastMessage, showErrorToastMessage } = useToastContext();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Refs for toast functions to keep handleCycleSelected stable (toast context functions are not memoized)
  const showSuccessRef = useRef(showSuccessToastMessage);
  showSuccessRef.current = showSuccessToastMessage;
  const showErrorRef = useRef(showErrorToastMessage);
  showErrorRef.current = showErrorToastMessage;

  const [loading, setLoading] = useState(true);

  const [receivedData, setReceivedData] = useState({ data: [], realtimeNow: false, dataList: [], chartDataTypes: [] });  
  const [calculatedDataUrl, setCalculatedDataUrl] = useState(null);    
  const [timeViewMode, setTimeViewMode] = useState(1); // 0 = from zero, 1 = actual time

  const [updatedMaxLengthSeconds, setUpdatedMaxLengthSeconds] = useState(0);
  
  const processedDataComponentRef = useRef(null);
  const recordInfoComponentRef = useRef(null);

  const loadOneRecord = (currentTimeType) => {
    api
      .get(`${baseOneRecordUrl}/${recordUid}?timeType=${currentTimeType}`)
      .then((response) => {
        setReceivedData(response.data);
        setLoading(false);
      }, (error) => {
        setLoading(false);
      });
  };

  useEffect(() => {   
    loadOneRecord(timeViewMode);
    setCalculatedDataUrl(`${baseOneRecordUrl}/${recordUid}/calculated`);
  }, [recordUid, userUid]);

  const setTimeType = (timeType) => {
    console.log('clog: UserViewOneRecord setTimeType called with:', timeType);
    
    // Clear old data to prevent mixed time formats
    setReceivedData({ data: [], realtimeNow: false, dataList: [], chartDataTypes: [] });
    
    // Set new time mode
    setTimeViewMode(timeType);
    
    // Load new data with correct time format
    loadOneRecord(timeType);
  };


  const onShowCalculatedDataClick = () => {
    if(!calculatedDataUrl)
      setCalculatedDataUrl(`${baseOneRecordUrl}/${recordUid}/calculated`);
  };

  // Handle cycle selection for expert consultation analysis
  // Memoized to prevent re-render cascade: onCycleSelected prop → handleCycleSelect → onRPeakClick → EcgPreviewChart
  const handleCycleSelected = useCallback(async (cycleNumber, cycleData, analyzeRequested) => {
    if (!consultationUid || !analyzeRequested) {
      // Just log for non-consultation contexts or non-analyze selections
      console.log('Cycle selected:', cycleNumber, cycleData);
      return;
    }

    // Call API to save selected cycle
    setIsAnalyzing(true);
    try {
      await api.post(`/consultation/expert/accepted/${consultationUid}/cycle/select`, {
        cycleNumber: cycleNumber,
        rPeakTimeMs: cycleData.timeMs
      });
      console.log('Cycle selection saved successfully');
      showSuccessRef.current(t('consultation.diagnosticData.analysisComplete'));
    } catch (error) {
      console.error('Failed to save cycle selection:', error);
      showErrorRef.current(t('consultation.diagnosticData.analysisError'));
    } finally {
      setIsAnalyzing(false);
    }
  }, [consultationUid, t]);

  if (loading)
    return <Loading />;

  const RecordDescriptionLine = ({ header, text }) => {
    return <Col lg><b>{header}:</b> {text}</Col>
  }

  const processedDataReceived = (processedData) => {
    //setUpdatedMaxLengthSeconds(lastProcessedSecond);
    processedDataComponentRef.current?.setLastProcessedTime(processedData?.lastProcessedSecond);
    recordInfoComponentRef.current?.setProcessedData(processedData);
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
        {/*<h1 className="h2">{response.user.displayName}</h1>    */}
      </div>
      {loading ? <Loading /> :
        <>
          {/* {allowRequestConsultation &&
            <Row className="mb-4">
              <Col>
                <RequestConsultationConfirmModal recordUid={recordUid} />
              </Col>
            </Row>
          }*/}

          {!consultationUid && (
            <RecordActionToolbar
              key={recordUid}
              userUid={userUid}
              recordUid={recordUid}
              description={receivedData.description}
              allowEdit={allowEditRecords}
              allowDelete={allowDeleteRecords}
              allowMove={allowMoveRecords}
              onRecordDeleted={() => navigate(getPageUserUrlTemplate ? getPageUserUrlTemplate(userUid) : `/user/${userUid}/records`)}
              onRecordMoved={() => navigate(getPageUserUrlTemplate ? getPageUserUrlTemplate(userUid) : `/user/${userUid}/records`)}
            />
          )}

          <UserViewOneRecordInfo ref={recordInfoComponentRef} description={receivedData.description} />         
         
          <>
            <Row className={`${styles.bt} mt-4`}>
              <Col>
                {receivedData.data && (
                  <>
                    <BaseEcgView 
                      ecgData={receivedData.data} 
                      realtimeDataUrl={receivedData.realtimeNow ? `/api/v1/front/user/records/${recordUid}/listen/websocket` : null}
                    />
                  </>
                )}
                {receivedData.dataList && <BaseMultipleChartsView
                  key={`charts-${timeViewMode}`}
                  dataList={receivedData.dataList}
                  chartDataTypes={receivedData.chartDataTypes}
                  realtimeDataUrl={receivedData.realtimeNow ? `/api/v1/front/user/records/${recordUid}/listen/websocket` : null}
                  baseRecordUrl={`${baseOneRecordUrl}/${recordUid}`}
                  timeViewMode={timeViewMode}
                  recordStartDate={receivedData.description?.date}
                  timeTypeChangeCallback={(x) => { setTimeType(x); }}
                  processedDataReceived={(x) => {
                    processedDataReceived(x);
                  }}
                  allowEditMarkers={allowEditMarkers}
                  onCycleSelected={consultationUid ? handleCycleSelected : undefined}
                  isAnalyzingCycle={isAnalyzing}
                />}

              </Col>
            </Row>
            <Row className="mt-5">
              <Col >
                <AllProcessedData 
                  ref={processedDataComponentRef} 
                  requestUrl={calculatedDataUrl} 
                  updatedMaxLengthSeconds={updatedMaxLengthSeconds}
                  timeViewMode={timeViewMode}
                  recordStartTime={receivedData.description?.date}
                ></AllProcessedData>                      

                {/*<Accordion flush>*/}
                {/*  <Accordion.Item eventKey="0" style={{ backgroundColor: 'lavender' }} >*/}
                {/*  */}{/*  <Accordion.Header </Accordion.Header>*/}
                {/*    <Accordion.Button onClick={onShowCalculatedDataClick} style={{ backgroundColor: 'lavender' }}>{t('temp.showCalculated')}</Accordion.Button>*/}
                {/*    <Accordion.Body>*/}
                {/*      <AllProcessedData requestUrl={calculatedDataUrl}></AllProcessedData>                      */}
                {/*    </Accordion.Body>*/}
                {/*  </Accordion.Item>                  */}
                {/*</Accordion>*/}
              </Col>
            </Row>
            {allowViewConsultation &&
              <Row className="mt-5">
                <Col>
                  <ViewConsultationList recordUid={recordUid} allowRequestConsultation={allowRequestConsultation} />
                </Col>
              </Row>}
          </>
        </>
      }
    </>
  );
}

